import React, { useMemo, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { useComponentTranslation } from "@/services/i18n";
import { FaFacebookSquare, FaArrowLeft, FaExternalLinkAlt } from "react-icons/fa";
import toast from "react-hot-toast";
import API from "@/services/api";
import PublishToggle from "./components/publish-toggle";

const translations = {
  en: {
    title: "Share on Facebook",
    description: "Share your job posting on Facebook to reach a wider audience",
    share_button: "Share on Facebook",
    back: "Back to channels",
    loading: "Loading...",
    post_preview: "Post Preview",
    post_text: "Post Text",
    post_text_placeholder: "Write your post message here...",
    edit_post: "Edit Post",
    hiring_text: "We're hiring!",
    apply_now: "Apply now:",
  },
  fr: {
    title: "Partager sur Facebook",
    description: "Partagez votre offre d'emploi sur Facebook pour atteindre un public plus large",
    share_button: "Partager sur Facebook",
    back: "Retour aux canaux",
    loading: "Chargement...",
    post_preview: "Aperçu du post",
    post_text: "Texte du post",
    post_text_placeholder: "Écrivez votre message ici...",
    edit_post: "Modifier le post",
    hiring_text: "Nous recrutons !",
    apply_now: "Postuler maintenant :",
  },
};

export default function FacebookShareChannel({ job, setJob, posts = [], setPosts }) {
  const { t } = useComponentTranslation(translations);
  const { id: jobId } = useParams();
  const navigate = useNavigate();
  const [channel, setChannel] = useState(null);
  const [loading, setLoading] = useState(true);
  const channelSlug = "share-on-facebook";

  useEffect(() => {
    fetchChannel();
  }, []);

  const fetchChannel = async () => {
    try {
      setLoading(true);
      const channelResponse = await API.post(`/channel/search`, {
        slug: channelSlug,
        limit: 1,
      });

      if (channelResponse.ok && channelResponse.data?.[0]) {
        setChannel(channelResponse.data[0]);
      }
    } catch (e) {
      console.error("Error loading channel:", error);
      toast.error(e.code);
    } finally {
      setLoading(false);
    }
  };

  // Find job post from posts array
  const jobPost = channel ? posts.find((post) => post.channel_id === channel._id) : null;

  const shareLink = useMemo(() => {
    if (!job?.organization_slug || !jobPost?._id) return "";
    return `https://jobego.co/companies/${job.organization_slug}/jobs/${jobPost._id}`;
  }, [job, jobPost]);

  const postText = useMemo(() => {
    if (!job?.title || !shareLink) return "";
    return `${t("hiring_text")} ${job.title}\n\n${t("apply_now")} ${shareLink}`;
  }, [job?.title, shareLink, t]);

  const handleShare = () => {
    if (!shareLink) {
      toast.error(e.code);
      return;
    }
    try {
      const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareLink)}&quote=${encodeURIComponent(postText)}`;
      window.open(facebookShareUrl, "_blank", "width=600,height=600,scrollbars=yes,resizable=yes");
    } catch (e) {
      console.error("Error opening Facebook share:", error);
      toast.error(e.code);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="ml-3 text-gray-600">{t("loading")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Back button */}
      <button onClick={() => navigate("..")} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4">
        <FaArrowLeft className="w-4 h-4" />
        {t("back")}
      </button>

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-start gap-4">
          {channel?.logo ? (
            <img src={channel.logo} alt={channel.name} className="w-16 h-16 rounded-lg object-cover shrink-0" />
          ) : (
            <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
              <FaFacebookSquare className="w-8 h-8 text-blue-600" />
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-2xl font-bold text-gray-900">{channel?.name || t("title")}</h3>
              {channel && <PublishToggle job={job} channel={channel} posts={posts} setPosts={setPosts} />}
            </div>

            <p className="text-sm text-gray-600 mb-6">{channel?.description || t("description")}</p>

            {jobPost && (
              <div className="flex items-center gap-2">
                <button onClick={handleShare} className="btn btn-primary flex items-center gap-2 bg-blue-600 hover:bg-blue-700 cursor-pointer">
                  <FaFacebookSquare className="w-4 h-4" />
                  {t("share_button")}
                  <FaExternalLinkAlt className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
